/**
 * Recorrido en vivo: cómo se pide y se une lo nuevo del equipo elegido
 * (especificación `specs/rastreo-registro/`, RR-18).
 *
 * El recorrido del período se pide entero una vez —hasta 2.000 puntos, del más
 * viejo al más nuevo— y después, mientras el período incluye el momento actual,
 * cada 5 s se pide solo la cola: desde el último punto conocido, con un minuto
 * de solape, y nunca más de 10 minutos atrás. Así cada consulta trae unos pocos
 * puntos y no el día completo.
 *
 * Lo nuevo se guarda aparte del recorrido base: cuando el base se relee, la cola
 * se recorta a lo que el base todavía no tiene. Entre el último punto del base y el
 * primero en vivo puede faltar un tramo, y el mapa no lo une con una recta (ese punto
 * lleva `breakBefore`):
 * - si el base venía recortado;
 * - si la primera consulta de la cola empezó después del último punto del base, porque
 *   el vivo estuvo apagado más de 10 minutos (lista en celular, pestaña oculta) y la
 *   ventana no llega tan atrás. Al volver, el panel relee el base y el hueco se llena.
 *
 * Puro: la hora llega como argumento (constitución, Art. 1).
 */

import type { RastreoPosition, RastreoVehicle } from "../contracts/rastreo.ts";

/** Cada cuánto se pide la cola del recorrido del equipo elegido. */
export const LIVE_REFRESH_MS = 5_000;
/** Con el vivo encendido, el recorrido completo se relee solo cada 5 minutos. */
export const LIVE_FULL_REFRESH_MS = 5 * 60_000;
/** La cola nunca pide más atrás de esto, aunque el último punto sea más viejo. */
export const LIVE_LOOKBACK_MS = 10 * 60_000;
/** Solape con el último punto: cubre tramas que llegan un poco tarde al motor. */
export const LIVE_OVERLAP_MS = 60_000;
/** Margen hacia adelante: un reloj de equipo algo adelantado no deja puntos fuera. */
export const LIVE_AHEAD_MS = 60_000;
/** Tope de puntos en vivo guardados en el navegador: se quedan los más nuevos. */
export const LIVE_MAX_POINTS = 2_000;

export type Period = { fromIso: string; toIso: string };

/** La hora con la que el motor ordena: la del equipo y, si no la mandó, la de llegada. */
export function pointTime(point: RastreoPosition): number {
  return Date.parse(point.deviceTime ?? point.serverReceivedAt);
}

/** Dos filas con la misma llegada, guardado y coordenadas son la misma trama. */
function pointKey(point: RastreoPosition): string {
  return `${point.serverReceivedAt}|${point.storedAt}|${point.latitude}|${point.longitude}`;
}

function lastTime(points: readonly RastreoPosition[]): number | null {
  let latest: number | null = null;
  for (const point of points) {
    const time = pointTime(point);
    if (Number.isFinite(time) && (latest === null || time > latest)) latest = time;
  }
  return latest;
}

/** El vivo solo tiene sentido si el período incluye el momento actual. */
export function isLivePeriod(period: Period | null, nowMs: number): boolean {
  if (!period) return false;
  const from = Date.parse(period.fromIso);
  const to = Date.parse(period.toIso);
  return Number.isFinite(from) && Number.isFinite(to) && from <= nowMs && nowMs < to;
}

/**
 * La ventana de la próxima consulta en vivo, o `null` si no toca pedir nada.
 * Desde: el último punto conocido menos el solape, sin pasar de 10 minutos atrás
 * ni del inicio del período. Hasta: un minuto adelante, sin pasar del fin del período.
 */
export function liveWindow(input: {
  period: Period | null;
  base: readonly RastreoPosition[];
  live: readonly RastreoPosition[];
  nowMs: number;
}): Period | null {
  const { period, nowMs } = input;
  if (!period || !isLivePeriod(period, nowMs)) return null;
  const known = lastTime(input.live) ?? lastTime(input.base);
  const floor = Math.max(Date.parse(period.fromIso), nowMs - LIVE_LOOKBACK_MS);
  const from = known === null ? floor : Math.max(floor, known - LIVE_OVERLAP_MS);
  const to = Math.min(Date.parse(period.toIso), nowMs + LIVE_AHEAD_MS);
  if (!(to > from)) return null;
  return { fromIso: new Date(from).toISOString(), toIso: new Date(to).toISOString() };
}

function sortByTime(points: RastreoPosition[]): RastreoPosition[] {
  return points
    .map((point, index) => ({ point, index, time: pointTime(point) }))
    .sort((a, b) => a.time - b.time || a.index - b.index)
    .map((item) => item.point);
}

/**
 * Suma a la cola en vivo lo que llegó: sin repetidos, solo lo posterior al último
 * punto del base, en orden y con tope (se quedan los más nuevos).
 */
export function appendLive(
  base: readonly RastreoPosition[],
  live: readonly RastreoPosition[],
  incoming: readonly RastreoPosition[],
): { live: RastreoPosition[]; added: number } {
  const baseLast = lastTime(base);
  const seen = new Set(base.map(pointKey));
  const kept: RastreoPosition[] = [];
  for (const point of live) {
    const time = pointTime(point);
    if (baseLast !== null && !(time > baseLast)) continue;
    if (seen.has(pointKey(point))) continue;
    seen.add(pointKey(point));
    kept.push(point);
  }
  let added = 0;
  for (const point of incoming) {
    const time = pointTime(point);
    if (!Number.isFinite(time)) continue;
    if (baseLast !== null && !(time > baseLast)) continue;
    if (seen.has(pointKey(point))) continue;
    seen.add(pointKey(point));
    kept.push(point);
    added += 1;
  }
  const sorted = sortByTime(kept);
  return { live: sorted.slice(Math.max(0, sorted.length - LIVE_MAX_POINTS)), added };
}

export type CombinedTrackPoint = { position: RastreoPosition; breakBefore: boolean };

/**
 * Desde cuándo cubre la cola: el inicio de la ventana con que entró su primer punto. Se conserva
 * mientras la cola tenga puntos y vuelve a `null` cuando queda vacía.
 */
export function tailStart(previousStart: number | null, previous: number, next: number, windowFrom: string): number | null {
  if (next === 0) return null;
  if (previous === 0) {
    const from = Date.parse(windowFrom);
    return Number.isFinite(from) ? from : null;
  }
  return previousStart;
}

/**
 * El recorrido que se dibuja: el base y, detrás, la cola en vivo que el base
 * todavía no tiene. El primer punto en vivo no se une con el anterior (`breakBefore`)
 * si el base venía recortado, o si la cola empezó a cubrir después del último punto
 * del base (`liveFrom`, de `tailStart`): en el medio puede faltar un tramo.
 */
export function combineTrack(
  base: { points: readonly RastreoPosition[]; truncated: boolean } | null,
  live: readonly RastreoPosition[],
  liveFrom: number | null = null,
): CombinedTrackPoint[] {
  const basePoints = base?.points ?? [];
  const baseLast = lastTime(basePoints);
  const seen = new Set(basePoints.map(pointKey));
  const tail = live.filter((point) => (baseLast === null || pointTime(point) > baseLast) && !seen.has(pointKey(point)));
  const combined: CombinedTrackPoint[] = basePoints.map((position) => ({ position, breakBefore: false }));
  const uncovered = liveFrom !== null && baseLast !== null && baseLast < liveFrom;
  tail.forEach((position, index) => {
    combined.push({
      position,
      breakBefore: index === 0 && basePoints.length > 0 && (Boolean(base?.truncated) || uncovered),
    });
  });
  return combined;
}

/**
 * El punto válido más nuevo del recorrido combinado. Con la misma regla del motor
 * para la «última posición»: un punto sin fix válido no mueve el marcador.
 */
export function latestValidPoint(points: readonly CombinedTrackPoint[]): RastreoPosition | null {
  let latest: RastreoPosition | null = null;
  let latestTime = Number.NEGATIVE_INFINITY;
  for (const { position } of points) {
    if (position.fixValid !== true) continue;
    const time = pointTime(position);
    if (Number.isFinite(time) && time >= latestTime) {
      latest = position;
      latestTime = time;
    }
  }
  return latest;
}

/**
 * El vehículo con la posición que llegó en vivo, si es más nueva que la que trajo
 * la flota. La frescura se recalcula con el mismo umbral del motor; sin umbral se
 * deja la que dijo el motor.
 */
export function withLivePosition(
  vehicle: RastreoVehicle,
  latest: RastreoPosition | null,
  nowMs: number,
  freshnessS: number | null,
): RastreoVehicle {
  if (!latest) return vehicle;
  const time = pointTime(latest);
  if (!Number.isFinite(time)) return vehicle;
  if (vehicle.lastPosition && pointTime(vehicle.lastPosition) >= time) return vehicle;
  return {
    ...vehicle,
    lastPosition: latest,
    lastTelemetryAt: new Date(time).toISOString(),
    telemetryFreshness:
      freshnessS === null ? vehicle.telemetryFreshness : nowMs - time <= freshnessS * 1000 ? "fresh" : "stale",
  };
}
